// ── Project State Management ────────────────────────────────────────
// Centralized state object, undo/redo, copy/paste, save/load, cloud sync

import {
  getArcData, migrateSprinkler, migrateZone,
} from '../calculations/logic.ts';
import type { LandscapeObjectDef, WaterSourceProperties } from '../calculations/types.ts';
import { showToast, authenticatedFetch } from '../shared/utils.ts';
import { trackProjectCreated } from '../shared/analytics.ts';
import { isAuthenticated } from '../shared/auth.ts';
export { showToast };
import { api_v1_project_path, api_v1_projects_path } from '../shared/api-routes';
import { LANDSCAPE_OBJECTS } from '../catalog/catalog-data.ts';
import { t } from '../shared/i18n.ts';
import type { Point } from '../calculations/geometry-utils.ts';
import { toFieldText, type UnitSystem } from '../shared/units.ts';

// ── State Types ───────────────────────────────────────────────────
export interface StateSprinkler {
  id: number;
  name?: string;
  model: string;
  arc: number;
  rotation: number;
  radius?: number;
  x: number;
  y: number;
  zoneId: number | null;
  bodyModel?: string | null;
  _manualZone?: boolean;
}

/** Build a head in the one shape the renderer expects.
 *
 * Three call sites used to assemble this object by hand — the canvas click
 * handler, the AUTO button, and the `addSprinklers` test/template hook — and
 * two of them left out `radius` and `name`. A head without a radius draws no
 * coverage ring and makes both the sidebar and status bar report uniformity
 * as "NaN%"; without a name its label renders blank. Going through one
 * factory keeps the three in step. */
export function createSprinkler(input: {
  id: number;
  x: number;
  y: number;
  model: string;
  arc?: number;
  rotation?: number;
  radius?: number;
  zoneId?: number | null;
}): StateSprinkler {
  const arc = input.arc ?? 360;
  return {
    id: input.id,
    name: `F${input.id}`,
    model: input.model,
    x: input.x,
    y: input.y,
    // Callers that already know the radius (auto-placement computes one per
    // head) keep it; everyone else takes the catalog value for the arc.
    radius: input.radius ?? getArcData(input.model, arc).radius,
    arc,
    rotation: input.rotation ?? 0,
    zoneId: input.zoneId ?? null,
    _manualZone: false,
  };
}

export interface StateZone {
  id: number;
  name: string;
  points: Point[];
  valveModel?: string | null;
  color?: string;
}

export interface StatePipe {
  id: number;
  name?: string;
  diameter: number;
  material?: string;
  zoneId?: number | null;
  points: Point[];
  startSnap?: { type: string; id?: number } | null;
  endSnap?: { type: string; id?: number } | null;
}

export interface StateDripLine {
  id: number;
  name?: string;
  model: string;
  zoneId?: number | null;
  points: Point[];
}

export interface StateTree {
  id: number;
  name?: string;
  type: string;
  shape?: string;
  x: number;
  y: number;
  radius?: number;
  width?: number;
  length?: number;
  rotation?: number;
  crownRadius?: number;
  waterSourceProps?: WaterSourceProperties;
}

export interface StateValveObject {
  id: number;
  name?: string;
  model: string;
  x: number;
  y: number;
  zoneId?: number | null;
}

export interface StateWeatherStation {
  id: number;
  name?: string;
  model: string;
  x: number;
  y: number;
}

export interface StateGuide {
  id: number;
  axis: "x" | "y";
  position: number;
}

export interface Snapshot {
  boundary: Point[];
  sprinklers: StateSprinkler[];
  zones: StateZone[];
  pipes: StatePipe[];
  dripLines: StateDripLine[];
  trees: StateTree[];
  valveObjects: StateValveObject[];
  weatherStations: StateWeatherStation[];
  guides: StateGuide[];
  nextSprinklerId: number;
  nextZoneId: number;
  nextPipeId: number;
  nextDripLineId: number;
  nextTreeId: number;
  nextValveObjId: number;
  nextWeatherStationId: number;
  nextGuideId: number;
}

/**
 * Unified coverage view mode. Replaces the separate `showCoverage` /
 * `showHeatmap` booleans with a single state field that can take three
 * values:
 *
 *   - `'off'`     — no coverage rendering
 *   - `'rings'`   — translucent reach circles per sprinkler (default)
 *   - `'heatmap'` — precipitation intensity raster overlay
 *
 * Refactor #1204: previously the planner exposed two duplicate checkboxes
 * (one in the context bar, one in the flyout Layers panel) for what is
 * conceptually the same data layer. The new `coverageView` field is the
 * single source of truth; legacy booleans are derived from it for
 * backward-compat with stored project JSON.
 */
export type CoverageView = 'off' | 'rings' | 'heatmap';

// ── State ──────────────────────────────────────────────────────────
export const state = {
  mode: "select",
  pxPerMeter: 40,
  gridSize: 0.5,
  showGrid: true,
  snapToGrid: true,
  // Authoritative coverage layer mode (rings | heatmap | off). The
  // legacy showCoverage / showHeatmap booleans below are derived
  // accessors for stored project data and external code that hasn't yet
  // migrated. New callers must read/write `coverageView`.
  coverageView: 'rings' as CoverageView,
  plotWidth: 20,
  plotHeight: 15,

  activeModel: "MP1000",
  activeArc: 360,

  boundary: [] as Point[],
  sprinklers: [] as StateSprinkler[],
  zones: [] as StateZone[],
  pipes: [] as StatePipe[],
  nextPipeId: 1,
  selectedPipe: null as number | null,
  tempPipe: [] as Point[],
  activePipeDiameter: 25,

  dripLines: [] as StateDripLine[],
  nextDripLineId: 1,
  selectedDripLine: null as number | null,
  tempDripLine: [] as Point[],
  activeDripModel: "Hunter HDL",

  trees: [] as StateTree[],
  nextTreeId: 1,
  selectedTree: null as number | null,

  valveObjects: [] as StateValveObject[],
  nextValveObjId: 1,
  selectedValveObj: null as number | null,

  weatherStations: [] as StateWeatherStation[],
  nextWeatherStationId: 1,
  selectedWeatherStation: null as number | null,

  guides: [] as StateGuide[],
  nextGuideId: 1,
  showGuides: true,

  selectedSprinkler: null as number | null,
  selectedZone: null as number | null,
  nextSprinklerId: 1,
  nextZoneId: 1,

  tempBoundary: [] as Point[],
  tempZone: [] as Point[],
  isDragging: false,
  dragTarget: null as unknown,
  dragType: null as string | null,
  dragIndex: null as number | null,
  dragOffset: { x: 0, y: 0 },

  viewBox: { x: -30, y: -30, w: 900, h: 700 },
  isPanning: false,
  spaceHeld: false,
  panStart: { x: 0, y: 0 },
  zoomLevel: 1,

  autoComponents: [] as unknown[],

  history: [] as Snapshot[],
  redoStack: [] as Snapshot[],
  maxHistory: 50,
  clipboard: null as { type: string; data: unknown } | null,

  // Multi-selection. Entries are "{type}:{id}".
  // Intentionally NOT included in Snapshot/createSnapshot/applySnapshot:
  // undo/redo restores element data, not transient UI selection state.
  multiSelection: new Set<string>(),

  // Active unit system for label/value rendering. Hydrated at planner boot
  // from `getUnits()` (which reads `window.__USER_UNITS` for signed-in
  // users / localStorage for anonymous), so the render layer doesn't
  // need to re-query global state on every paint. Anonymous default:
  // metric. Per issue #1431 (Phase 1).
  userUnits: 'metric' as UnitSystem,
};

// ── Coverage view (legacy boolean compat) ──────────────────────────
// Older callers and stored project JSON use `showCoverage` / `showHeatmap`
// booleans. We expose them as accessor properties on `state` so reads
// reflect the authoritative `coverageView` and writes update it. This
// keeps the transition incremental: callers can be migrated to the new
// field one at a time without breaking any existing flow.
//
// Mapping rules:
//   - `showCoverage` is true when a coverage layer is visible at all
//     (rings OR heatmap), false when 'off'.
//   - `showHeatmap` is true only in 'heatmap' mode.
//   - Writing `showHeatmap = true` flips to 'heatmap'. Writing it back
//     to false falls back to 'rings' if `showCoverage` was true,
//     otherwise 'off'. Writing `showCoverage = false` forces 'off'.
//
// The Object.defineProperty calls install non-enumerable accessors so
// JSON.stringify(state) does not duplicate the legacy keys (we already
// include them explicitly in `getProjectDataForAPI()` for compat).
Object.defineProperty(state, "showCoverage", {
  configurable: true,
  enumerable: false,
  get(): boolean { return state.coverageView !== 'off'; },
  set(value: boolean) {
    if (value) {
      // Don't downgrade from heatmap to rings if heatmap was active.
      if (state.coverageView === 'off') state.coverageView = 'rings';
    } else {
      state.coverageView = 'off';
    }
  },
});

Object.defineProperty(state, "showHeatmap", {
  configurable: true,
  enumerable: false,
  get(): boolean { return state.coverageView === 'heatmap'; },
  set(value: boolean) {
    if (value) {
      state.coverageView = 'heatmap';
    } else if (state.coverageView === 'heatmap') {
      // Fall back to rings — heatmap implied coverage was on.
      state.coverageView = 'rings';
    }
  },
});

/**
 * Coerce legacy `showCoverage` + `showHeatmap` flags into a `CoverageView`
 * value. Used when loading projects saved before #1204. Heatmap wins
 * over rings when both are true (the old UI rendered heatmap and hid
 * rings in that case — see the `if (state.showHeatmap)` branch in
 * `renderAll()`).
 */
export function deriveCoverageView({
  showCoverage,
  showHeatmap,
}: {
  showCoverage?: boolean;
  showHeatmap?: boolean;
}): CoverageView {
  if (showHeatmap) return 'heatmap';
  if (showCoverage === false) return 'off';
  return 'rings';
}

/**
 * Reflect `state.coverageView` into the unified DOM control rendered in
 * the Layers flyout (`#pl2-layer-coverage-view`). No-op when the control
 * is not in the DOM yet (e.g. during early bootstrap or in unit tests).
 */
export function syncCoverageViewControl(): void {
  const control = document.getElementById('pl2-layer-coverage-view');
  if (control && control instanceof HTMLSelectElement) {
    control.value = state.coverageView;
  }
}

/**
 * Element types valid for multi-selection. Kept as a string union so callers
 * get autocomplete; the runtime representation in `multiSelection` is still a
 * "{type}:{id}" key for simple Set semantics.
 */
export type MultiSelectType =
  | 'sprinkler' | 'pipe' | 'dripLine' | 'tree'
  | 'valveObj' | 'weatherStation' | 'zone';

const multiSelectKey = (type: MultiSelectType, id: number) => `${type}:${id}`;

export function toggleMultiSelection(type: MultiSelectType, id: number): boolean {
  const key = multiSelectKey(type, id);
  if (state.multiSelection.has(key)) {
    state.multiSelection.delete(key);
    return false;
  }
  state.multiSelection.add(key);
  return true;
}

export function clearMultiSelection() {
  state.multiSelection.clear();
}

export function isMultiSelected(type: MultiSelectType, id: number): boolean {
  return state.multiSelection.has(multiSelectKey(type, id));
}

// Maps each multi-select type to the state array that holds it. When new
// element types are added, extend this single table rather than the filter
// logic in deleteMultiSelection().
const ARRAYS_BY_TYPE: Record<MultiSelectType, keyof typeof state> = {
  sprinkler: 'sprinklers',
  pipe: 'pipes',
  dripLine: 'dripLines',
  tree: 'trees',
  valveObj: 'valveObjects',
  weatherStation: 'weatherStations',
  zone: 'zones',
};

/** Delete all elements in the multi-selection set. Returns the count removed.
 *  Mutates each array in place (splice) instead of reassigning, so external
 *  modules that captured a reference (e.g. cached closures over state.sprinklers)
 *  continue to see the updated data. */
export function deleteMultiSelection(): number {
  if (state.multiSelection.size === 0) return 0;
  saveState();
  let deleted = 0;
  state.multiSelection.forEach((key) => {
    const sep = key.indexOf(':');
    const type = key.slice(0, sep) as MultiSelectType;
    const id = Number(key.slice(sep + 1));
    const arrKey = ARRAYS_BY_TYPE[type];
    if (!arrKey) return;
    const arr = state[arrKey] as Array<{ id: number }>;
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i].id === id) {
        arr.splice(i, 1);
        deleted++;
      }
    }
  });
  state.multiSelection.clear();
  clearSelection();
  return deleted;
}

// ── Selection ──────────────────────────────────────────────────────
export function clearSelection() {
  state.selectedSprinkler = null;
  state.selectedZone = null;
  state.selectedPipe = null;
  state.selectedTree = null;
  state.selectedValveObj = null;
  state.selectedDripLine = null;
  state.selectedWeatherStation = null;
  state.multiSelection.clear();
}

// ── History (Undo / Redo) ───────────────────────────────────────────
export function createSnapshot() {
  return {
    boundary: JSON.parse(JSON.stringify(state.boundary)),
    sprinklers: JSON.parse(JSON.stringify(state.sprinklers)),
    zones: JSON.parse(JSON.stringify(state.zones)),
    pipes: JSON.parse(JSON.stringify(state.pipes)),
    dripLines: JSON.parse(JSON.stringify(state.dripLines)),
    trees: JSON.parse(JSON.stringify(state.trees)),
    valveObjects: JSON.parse(JSON.stringify(state.valveObjects)),
    weatherStations: JSON.parse(JSON.stringify(state.weatherStations)),
    guides: JSON.parse(JSON.stringify(state.guides)),
    nextSprinklerId: state.nextSprinklerId,
    nextZoneId: state.nextZoneId,
    nextPipeId: state.nextPipeId,
    nextDripLineId: state.nextDripLineId,
    nextTreeId: state.nextTreeId,
    nextValveObjId: state.nextValveObjId,
    nextWeatherStationId: state.nextWeatherStationId,
    nextGuideId: state.nextGuideId,
  };
}

export function applySnapshot(snapshot: Partial<Snapshot>) {
  Object.assign(state, {
    boundary: snapshot.boundary,
    sprinklers: snapshot.sprinklers,
    zones: snapshot.zones,
    pipes: snapshot.pipes,
    dripLines: snapshot.dripLines || [],
    trees: snapshot.trees || [],
    valveObjects: snapshot.valveObjects || [],
    weatherStations: snapshot.weatherStations || [],
    guides: snapshot.guides || [],
    nextSprinklerId: snapshot.nextSprinklerId,
    nextZoneId: snapshot.nextZoneId,
    nextPipeId: snapshot.nextPipeId,
    // Use nullish coalescing so a legitimately-zero nextId from the snapshot
    // is preserved. Previously `|| state.nextDripLineId` treated 0 as falsy
    // and fell back to the current counter, breaking id monotonicity when
    // undoing back to an empty project.
    nextDripLineId: snapshot.nextDripLineId ?? state.nextDripLineId,
    nextTreeId: snapshot.nextTreeId ?? state.nextTreeId,
    nextValveObjId: snapshot.nextValveObjId ?? state.nextValveObjId,
    nextWeatherStationId: snapshot.nextWeatherStationId ?? state.nextWeatherStationId,
    nextGuideId: snapshot.nextGuideId ?? state.nextGuideId,
    selectedSprinkler: null,
    selectedZone: null,
    selectedPipe: null,
    selectedDripLine: null,
    selectedTree: null,
    selectedValveObj: null,
    selectedWeatherStation: null,
  });
}

/** Fast-path deep equality for snapshots. Both snapshots are produced by
 *  createSnapshot() (JSON-safe structure), so JSON.stringify is a correct and
 *  cheap check compared to a custom deep-walk. Used by saveState() to suppress
 *  no-op history entries (e.g. a mousedown that never moved the target). */
function snapshotEquals(a: Snapshot, b: Snapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Hook invoked once per session on the first saveState() that produces a real
// mutation (dedup passed). Bootstraps a cloud draft so that sessions started
// without `?project=ID` still get persisted. Set by app.ts after init so that
// state.ts remains testable/mockable without a DOM.
let firstMutationHook: (() => void) | null = null;
export function setFirstMutationHook(hook: (() => void) | null) {
  firstMutationHook = hook;
}

export function saveState() {
  const snap = createSnapshot();
  // Skip pushing a duplicate snapshot. Without this guard, undo/redo becomes
  // effectively broken because every mousedown (even a click without a drag)
  // pushes a duplicate of the latest history entry AND clears the redo stack,
  // so the user has to undo many times to see any real change.
  const last = state.history[state.history.length - 1];
  if (last && snapshotEquals(last, snap)) return;
  commitHistory(snap);
}

/**
 * Push a snapshot onto the undo stack and notify the bootstrap layer.
 *
 * `onCanvasMouseUp` commits its pre-drag snapshot rather than calling
 * `saveState()`, because the snapshot it wants is the state from *before* the
 * drag started. It used to push onto `state.history` directly, which skipped
 * the first-mutation hook — so a session spent only dragging existing elements
 * never created its cloud draft. An authenticated user opening `/uk/planner`
 * with no `?project=ID`, whose previous session was restored from
 * localStorage, could reposition heads for an entire sitting with
 * `currentProjectId` still null and nothing ever reaching the server.
 */
export function commitHistory(snapshot: Snapshot) {
  state.history.push(snapshot);
  if (state.history.length > state.maxHistory) state.history.shift();
  state.redoStack = [];

  // Notify the bootstrap layer that a real mutation happened — used to
  // lazily create a cloud draft for anonymous-URL sessions (/app without
  // ?project=ID). One-shot: the hook clears itself after firing.
  if (firstMutationHook) {
    const hook = firstMutationHook;
    firstMutationHook = null;
    try { hook(); } catch (err) { console.error("firstMutationHook failed:", err); }
  }
}

export function undo(renderAll: () => void) {
  if (state.history.length === 0) return;
  state.redoStack.push(createSnapshot());
  applySnapshot(state.history.pop()!);
  renderAll();
}

export function redo(renderAll: () => void) {
  if (state.redoStack.length === 0) return;
  state.history.push(createSnapshot());
  applySnapshot(state.redoStack.pop()!);
  renderAll();
}

// ── Copy / Paste ───────────────────────────────────────────────────
const PASTE_OFFSET = 1.0;

export function copySelected() {
  if (state.selectedSprinkler) {
    const sp = state.sprinklers.find((s) => s.id === state.selectedSprinkler);
    if (sp) state.clipboard = { type: "sprinkler", data: JSON.parse(JSON.stringify(sp)) };
  } else if (state.selectedTree) {
    const tree = state.trees.find((item) => item.id === state.selectedTree);
    if (tree) state.clipboard = { type: "tree", data: JSON.parse(JSON.stringify(tree)) };
  } else if (state.selectedValveObj) {
    const vo = state.valveObjects.find((item) => item.id === state.selectedValveObj);
    if (vo) state.clipboard = { type: "valveObj", data: JSON.parse(JSON.stringify(vo)) };
  } else if (state.selectedDripLine) {
    const dl = state.dripLines.find((item) => item.id === state.selectedDripLine);
    if (dl) state.clipboard = { type: "dripLine", data: JSON.parse(JSON.stringify(dl)) };
  } else if (state.selectedPipe) {
    const pipe = state.pipes.find((item) => item.id === state.selectedPipe);
    if (pipe) state.clipboard = { type: "pipe", data: JSON.parse(JSON.stringify(pipe)) };
  }
}

export function pasteClipboard({ autoAssignSprinklersToZones, selectSprinkler, selectTree, selectValveObj, renderAll, t: _translate }: { autoAssignSprinklersToZones: () => void; selectSprinkler: (id: number) => void; selectTree: (id: number) => void; selectValveObj: (id: number) => void; renderAll: () => void; t: unknown }) {
  if (!state.clipboard) return;
  saveState();
  const d = JSON.parse(JSON.stringify(state.clipboard.data));

  switch (state.clipboard.type) {
    case "sprinkler": {
      const id = state.nextSprinklerId++;
      d.id = id; d.name = t("defaults.sprinklerName", { id });
      d.x += PASTE_OFFSET; d.y += PASTE_OFFSET;
      d.zoneId = null; d._manualZone = false;
      state.sprinklers.push(d);
      autoAssignSprinklersToZones();
      selectSprinkler(id);
      break;
    }
    case "tree": {
      const id = state.nextTreeId++;
      d.id = id; d.name = t("defaults.treeName", { id });
      d.x += PASTE_OFFSET; d.y += PASTE_OFFSET;
      state.trees.push(d);
      selectTree(id);
      break;
    }
    case "valveObj": {
      const id = state.nextValveObjId++;
      d.id = id; d.name = t("defaults.valveName", { id });
      d.x += PASTE_OFFSET; d.y += PASTE_OFFSET;
      state.valveObjects.push(d);
      selectValveObj(id);
      break;
    }
    case "pipe": {
      const id = state.nextPipeId++;
      d.id = id; d.name = t("defaults.pipeName", { id });
      d.points = d.points.map((p: { x: number; y: number }) => ({ x: p.x + PASTE_OFFSET, y: p.y + PASTE_OFFSET }));
      d.startSnap = null; d.endSnap = null;
      state.pipes.push(d);
      clearSelection();
      state.selectedPipe = id;
      renderAll();
      break;
    }
    case "dripLine": {
      const id = state.nextDripLineId++;
      d.id = id; d.name = t("defaults.dripName", { id });
      d.points = d.points.map((p: { x: number; y: number }) => ({ x: p.x + PASTE_OFFSET, y: p.y + PASTE_OFFSET }));
      d.zoneId = null;
      state.dripLines.push(d);
      clearSelection();
      state.selectedDripLine = id;
      renderAll();
      break;
    }
  }
}

// ── Export Helpers ──────────────────────────────────────────────────
export function getProjectDataForAPI() {
  return {
    version: 5, plotWidth: state.plotWidth, plotHeight: state.plotHeight,
    pxPerMeter: state.pxPerMeter, gridSize: state.gridSize,
    // Persist the new field alongside the legacy booleans so older
    // clients (or rollback to a previous release) still load saved
    // projects correctly.
    coverageView: state.coverageView,
    showCoverage: state.coverageView !== 'off',
    showHeatmap: state.coverageView === 'heatmap',
    // `loadProjectFromAPI` reads all six of these back out of `data`, but the
    // save payload never wrote them — only the localStorage autosave did. So
    // a cloud project reopened the grid, snap, guides and active nozzle / arc
    // / pipe-diameter at their defaults every time, silently discarding the
    // toggles the user had set. The backend stores `data` as a free-form
    // JSONB hash (Projects::UpdateContract), so the extra keys just persist.
    showGrid: state.showGrid,
    snapToGrid: state.snapToGrid,
    showGuides: state.showGuides,
    activeModel: state.activeModel,
    activeArc: state.activeArc,
    activePipeDiameter: state.activePipeDiameter,
    boundary: state.boundary, sprinklers: state.sprinklers, zones: state.zones,
    pipes: state.pipes, dripLines: state.dripLines, trees: state.trees,
    valveObjects: state.valveObjects, weatherStations: state.weatherStations,
    guides: state.guides,
    nextSprinklerId: state.nextSprinklerId, nextZoneId: state.nextZoneId,
    nextPipeId: state.nextPipeId, nextDripLineId: state.nextDripLineId,
    nextTreeId: state.nextTreeId, nextValveObjId: state.nextValveObjId,
    nextWeatherStationId: state.nextWeatherStationId, nextGuideId: state.nextGuideId,
  };
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}


// ── Cloud Project API ───────────────────────────────────────────────
let currentProjectId: number | null = null;
// Kept for the title bar, so it need not download the project again.
let currentProjectName: string | null = null;
let cloudSaveTimer: ReturnType<typeof setTimeout> | null = null;
let cloudSaveBackoffMs = 10000;
const CLOUD_SAVE_BASE_MS = 10000;
// The plan as the server last accepted it (null until a load, draft or save
// succeeds). The unload handler compares against it to know whether leaving
// would lose edits.
let lastCloudSavedJson: string | null = null;
// The project's `updated_at` as the server last reported it. Sent with every
// save so a copy another tab or device has since overwritten is refused
// (409) instead of silently winning (#1779).
let currentProjectVersion: string | null = null;
const CLOUD_SAVE_MAX_BACKOFF_MS = 300000;

export function getCurrentProjectId(): number | null { return currentProjectId; }
export function getCurrentProjectName(): string | null { return currentProjectName; }

type StoredRow = Record<string, unknown>;
type StoredCollection = "sprinklers" | "zones" | "pipes" | "dripLines" | "trees" | "valveObjects" | "weatherStations";

const isStoredNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * Give every stored element a numeric id, and carry the references between
 * elements along with it.
 *
 * Ids that are not finite numbers are renumbered: seeded and legacy string ids
 * ("spr-1", "zone-1"), and whatever else a payload carries — a shared template
 * could hold arrays, which stringify straight into the renderer's attribute
 * positions. Heads, valves, pipes and drip lines name their zone by id, and a
 * pipe the head, valve or water source it is snapped to. Renumbering the ids
 * alone left every one of those on the old value, so a project made from a
 * marketplace template opened with each head in no zone.
 *
 * A new id is the row's position unless a numeric row already owns it. A
 * reference that resolves to nothing becomes null.
 */
export function normalizeStoredElements(data: Partial<Record<StoredCollection, unknown>>): Record<StoredCollection, StoredRow[]> {
  const sprinklers = renumberStored(data.sprinklers);
  const zones = renumberStored(data.zones);
  const pipes = renumberStored(data.pipes);
  const dripLines = renumberStored(data.dripLines);
  const trees = renumberStored(data.trees);
  const valveObjects = renumberStored(data.valveObjects);
  const weatherStations = renumberStored(data.weatherStations);

  for (const row of [...sprinklers.rows, ...valveObjects.rows, ...pipes.rows, ...dripLines.rows]) {
    if ("zoneId" in row) row.zoneId = resolveStoredRef(row.zoneId, zones.ids);
  }

  const snapTargets: Record<string, Map<string, number>> = {
    sprinkler: sprinklers.ids, valveObj: valveObjects.ids, waterSource: trees.ids,
  };
  for (const pipe of pipes.rows) {
    for (const end of ["startSnap", "endSnap"] as const) {
      const snap = pipe[end] as { type?: unknown; id?: unknown } | null | undefined;
      const ids = snap ? snapTargets[String(snap.type)] : undefined;
      if (!snap || !ids) continue;
      const id = resolveStoredRef(snap.id, ids);
      pipe[end] = id === null ? null : { ...snap, id };
    }
  }

  // Seeded zones also list their heads.
  for (const zone of zones.rows) {
    if (Array.isArray(zone.sprinklerIds)) {
      zone.sprinklerIds = zone.sprinklerIds
        .map((id) => resolveStoredRef(id, sprinklers.ids))
        .filter((id) => id !== null);
    }
  }

  return {
    sprinklers: sprinklers.rows, zones: zones.rows, pipes: pipes.rows, dripLines: dripLines.rows,
    trees: trees.rows, valveObjects: valveObjects.rows, weatherStations: weatherStations.rows,
  };
}

function renumberStored(raw: unknown): { rows: StoredRow[]; ids: Map<string, number> } {
  const rows = (Array.isArray(raw) ? raw : []) as StoredRow[];
  const taken = new Set(rows.map((row) => row.id).filter(isStoredNumber));
  const ids = new Map<string, number>();
  rows.forEach((row, i) => {
    if (isStoredNumber(row.id)) return;
    let id = i + 1;
    while (taken.has(id)) id++;
    taken.add(id);
    if (typeof row.id === "string") ids.set(row.id, id);
    row.id = id;
  });
  return { rows, ids };
}

function boundaryExtent(boundary: Point[], axis: "x" | "y"): number {
  return boundary.reduce((max, p) => Math.max(max, Number(p[axis]) || 0), 0);
}

function resolveStoredRef(ref: unknown, ids: Map<string, number>): number | null {
  if (typeof ref === "string" && ids.has(ref)) return ids.get(ref)!;
  return isStoredNumber(ref) ? ref : null;
}

export async function loadProjectFromAPI(projectId: number | string, { $, renderAll, updateToolbarPreset, zoomToFit, startCloudAutoSave: doStartCloudAutoSave }: { $: (sel: string) => HTMLElement; renderAll: () => void; updateToolbarPreset: () => void; zoomToFit: () => void; startCloudAutoSave: () => void }) {
  
  if (!isAuthenticated()) return false;

  try {
    const resp = await authenticatedFetch(api_v1_project_path(projectId));
    if (!resp.ok) return false;

    const project = await resp.json();
    currentProjectId = project.id;
    currentProjectName = project.name ?? null;
    currentProjectVersion = project.updated_at ?? null;

    const data = project.data || {};
    state.boundary = data.boundary || [];
    // Seeded templates and sketch imports store a boundary but no plot size,
    // and the grid and zoomToFit frame the plot: a 60 x 50 m field opened as
    // a 20 x 15 m corner of itself. Frame it round the boundary the way
    // applyBoundary does.
    state.plotWidth = data.plotWidth || Math.max(20, Math.ceil(boundaryExtent(state.boundary, "x") + 2));
    state.plotHeight = data.plotHeight || Math.max(15, Math.ceil(boundaryExtent(state.boundary, "y") + 2));
    state.pxPerMeter = data.pxPerMeter || 40;
    state.gridSize = data.gridSize || 0.5;
    // Ids and the references between elements first, before any row is
    // migrated or named after its id.
    const stored: Record<string, any> = normalizeStoredElements(data);
    state.sprinklers = stored.sprinklers.map((sp: Record<string, unknown>) => {
      // @ts-expect-error — migrateSprinkler accepts SprinklerWithZone shape
      const m = migrateSprinkler(sp) as StateSprinkler;
      if (!m.name) m.name = t("defaults.sprinklerName", { id: m.id });
      return m;
    });
    state.zones = stored.zones.map((z: Record<string, unknown>) => {
      // @ts-expect-error — migrateZone accepts the raw stored shape
      return migrateZone(z);
    });
    state.pipes = stored.pipes.map((p: Record<string, unknown>) => {
      // @ts-expect-error — TS strict
      if (!p.name) p.name = t("defaults.pipeName", { id: p.id });
      return p;
    }) as StatePipe[];
    state.trees = stored.trees.map((tr: Record<string, unknown>) => {
      // @ts-expect-error — TS strict
      if (!tr.name) tr.name = t("defaults.treeName", { id: tr.id });
      if (tr.crownRadius && !tr.radius) { tr.radius = tr.crownRadius; delete tr.crownRadius; }
      // LANDSCAPE_OBJECTS is Record<string, LandscapeObjectDef> — a discriminated
      // union by `shape`. Narrow per-branch instead of flattening with double-cast.
      const def: LandscapeObjectDef | undefined = LANDSCAPE_OBJECTS[tr.type as string];
      if (!tr.shape) tr.shape = def?.shape || 'circle';
      if (tr.shape === 'circle' && !tr.radius) {
        tr.radius = (def?.shape === 'circle' && def.defaultRadius) || 1.5;
      }
      if (tr.shape === 'rect') {
        if (!tr.width) tr.width = (def?.shape === 'rect' && def.defaultWidth) || 2;
        if (!tr.length) tr.length = (def?.shape === 'rect' && def.defaultLength) || 2;
        if (tr.rotation === undefined) tr.rotation = 0;
      }
      return tr;
    }) as StateTree[];
    state.valveObjects = stored.valveObjects.map((v: Record<string, unknown>) => {
      // @ts-expect-error — TS strict
      if (!v.name) v.name = t("defaults.valveName", { id: v.id });
      return v;
    }) as StateValveObject[];
    state.dripLines = stored.dripLines.map((dl: Record<string, unknown>) => {
      // @ts-expect-error — TS strict
      if (!dl.name) dl.name = t("defaults.dripName", { id: dl.id });
      return dl;
    }) as StateDripLine[];
    state.weatherStations = stored.weatherStations.map((ws: Record<string, unknown>) => {
      // @ts-expect-error — TS strict
      if (!ws.name) ws.name = t("defaults.weatherStationName", { id: ws.id });
      return ws;
    }) as StateWeatherStation[];
    state.guides = data.guides || [];
    // next*Id is a local monotonic counter. Loaded data may contain
    // legacy composite ids `(prefix * 1e7 + local)` from when collaborative
    // editing was active (parked in 2026-04-27 Yjs cleanup). Strip the
    // prefix to recover the local part so new inserts don't collide with
    // existing entities — even after we stopped generating composites.
    const localPart = (id: number) => (id || 0) % 10_000_000;
    const maxSp = state.sprinklers.reduce((m, s) => Math.max(m, localPart(s.id)), 0);
    const maxPi = state.pipes.reduce((m, p) => Math.max(m, localPart(p.id)), 0);
    const maxDl = state.dripLines.reduce((m, d) => Math.max(m, localPart(d.id)), 0);
    const maxTr = state.trees.reduce((m, item) => Math.max(m, localPart(item.id)), 0);
    const maxVo = state.valveObjects.reduce((m, v) => Math.max(m, localPart(v.id)), 0);
    const maxWs = state.weatherStations.reduce((m, w) => Math.max(m, localPart(w.id)), 0);
    // Zones were the one collection whose counter fell back to 1 instead of
    // the highest stored id, so a project saved before `nextZoneId` was
    // persisted handed the next drawn zone an id an existing zone already had.
    const maxZn = state.zones.reduce((m, z) => Math.max(m, localPart(z.id)), 0);
    // Guides never had the same treatment. They carry ids precisely so they
    // can be addressed individually, so a duplicate makes one unremovable.
    const maxGu = state.guides.reduce((m, g) => Math.max(m, localPart(g.id)), 0);
    state.nextSprinklerId = data.nextSprinklerId || maxSp + 1;
    state.nextZoneId = data.nextZoneId || maxZn + 1;
    state.nextPipeId = data.nextPipeId || maxPi + 1;
    state.nextDripLineId = data.nextDripLineId || maxDl + 1;
    state.nextTreeId = data.nextTreeId || maxTr + 1;
    state.nextValveObjId = data.nextValveObjId || maxVo + 1;
    state.nextWeatherStationId = data.nextWeatherStationId || maxWs + 1;
    state.nextGuideId = data.nextGuideId || maxGu + 1;
    clearSelection();

    if (data.showGrid !== undefined) state.showGrid = data.showGrid;
    if (data.snapToGrid !== undefined) state.snapToGrid = data.snapToGrid;
    // Coverage view: prefer the new field if present, otherwise derive
    // from the legacy showCoverage/showHeatmap booleans (#1204).
    if (data.coverageView !== undefined) {
      state.coverageView = data.coverageView as CoverageView;
    } else if (data.showCoverage !== undefined || data.showHeatmap !== undefined) {
      state.coverageView = deriveCoverageView({
        showCoverage: data.showCoverage,
        showHeatmap: data.showHeatmap,
      });
    }
    if (data.showGuides !== undefined) state.showGuides = data.showGuides;
    if (data.activeModel) state.activeModel = data.activeModel;
    if (data.activeArc) state.activeArc = data.activeArc;
    if (data.activePipeDiameter) state.activePipeDiameter = data.activePipeDiameter;

    ($("#plotWidth") as HTMLInputElement).value = toFieldText(state.plotWidth, "length", state.userUnits);
    ($("#plotHeight") as HTMLInputElement).value = toFieldText(state.plotHeight, "length", state.userUnits);
    ($("#pxPerMeter") as HTMLInputElement).value = String(state.pxPerMeter);
    ($("#gridSize") as HTMLInputElement).value = String(state.gridSize);
    // `showGrid` is mirrored into the Layers flyout checkbox (#1205);
    // the orphan `#showGrid` ctx-bar checkbox was removed in the same
    // PR. Guard against the layers flyout not being mounted (unit
    // tests, brand-page reuse, etc.) by falling back to a no-op.
    const layerGridCb = document.getElementById('pl2-layer-grid');
    if (layerGridCb instanceof HTMLInputElement) {
      layerGridCb.checked = state.showGrid;
    }
    ($("#snapToGrid") as HTMLInputElement).checked = state.snapToGrid;
    syncCoverageViewControl();
    ($("#showGuides") as HTMLInputElement).checked = state.showGuides;
    ($("#activePipeDiameter") as HTMLInputElement).value = String(state.activePipeDiameter);

    updateToolbarPreset();
    zoomToFit();
    renderAll();
    lastCloudSavedJson = JSON.stringify(getProjectDataForAPI());
    doStartCloudAutoSave();

    return true;
  } catch (err) {
    console.error('Failed to load project from API:', err);
    return false;
  }
}

let draftCreateInFlight = false;

/**
 * Create a cloud draft project for an anonymous-but-authenticated user who
 * started the planner without `?project=ID`. Called lazily on the first
 * meaningful mutation so we don't spam POST /projects for every visit.
 * Handles rate-limiting (429) and validation errors (422) with a toast and
 * leaves `currentProjectId` null so the caller can retry on the next tick.
 */
export async function ensureCloudDraft(): Promise<number | null> {
  if (currentProjectId) return currentProjectId;
  
  if (!isAuthenticated()) return null;
  if (draftCreateInFlight) return null;

  draftCreateInFlight = true;
  try {
    // Body is flat — `Projects::CreateContract` (app/contracts/projects/create_contract.rb)
    // expects `name` and `data` at the root, not under a `project:` wrapper.
    // Wrapping under `project:` silently dropped both keys, the contract rejected
    // with `name is missing`, and no draft was ever persisted (`currentProjectId`
    // stayed null so subsequent `saveProjectToAPI` calls returned "skip").
    const data = getProjectDataForAPI();
    const resp = await authenticatedFetch(api_v1_projects_path(), {
      method: "POST",
      body: { name: "Untitled draft", data },
    });
    if (resp.status === 429) {
      showToast(t("ai.rateLimitExceeded"), "warning");
      return null;
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => null);
      const msg = body?.details || body?.error || t("errors.projectSaveFailed");
      showToast(msg, "error");
      return null;
    }
    const project = await resp.json();
    currentProjectId = project.id;
    currentProjectName = project.name ?? null;
    currentProjectVersion = project.updated_at ?? null;
    lastCloudSavedJson = JSON.stringify(data);
    trackProjectCreated();
    return currentProjectId;
  } catch (err) {
    console.error("Draft project creation failed:", err);
    return null;
  } finally {
    draftCreateInFlight = false;
  }
}

/**
 * Save the current project to the backend. Returns:
 *  - "ok" on success (2xx)
 *  - "throttled" on 403/429 (caller should back off)
 *  - "rejected" on any other non-2xx — a 422, a 413 from the request-size
 *    limit, a 500 (caller should back off: the same payload keeps failing)
 *  - "error" when no answer came back (retry next tick at normal cadence)
 *  - "skip" when no project id / token is available
 *
 * `notify: false` suppresses the failure toast; the autosave loop uses it to
 * warn once per run of failures instead of on every retry.
 */
export async function saveProjectToAPI({ notify = true }: { notify?: boolean } = {}): Promise<"ok" | "throttled" | "rejected" | "conflict" | "error" | "skip"> {
  if (!currentProjectId) return "skip";

  if (!isAuthenticated()) return "skip";

  try {
    const data = getProjectDataForAPI();
    const resp = await authenticatedFetch(api_v1_project_path(currentProjectId), {
      method: "PATCH",
      body: currentProjectVersion ? { data, expected_updated_at: currentProjectVersion } : { data },
    });
    if (resp.status === 409) {
      if (notify) showToast(t("errors.projectChangedElsewhere"), "warning");
      return "conflict";
    }
    if (!resp.ok) {
      if (notify) {
        const body = await resp.json().catch(() => null);
        const msg = body?.details || body?.error || t("errors.projectSaveFailed");
        showToast(msg, resp.status === 403 ? "warning" : "error");
      }
      if (resp.status === 403 || resp.status === 429) return "throttled";
      return "rejected";
    }
    lastCloudSavedJson = JSON.stringify(data);
    const saved = await resp.json().catch(() => null);
    if (saved?.updated_at) currentProjectVersion = saved.updated_at;
    return "ok";
  } catch (err) {
    console.error('Project auto-save failed, will retry:', err);
    return "error";
  }
}

/**
 * Start (or restart) the cloud autosave loop.
 *
 * Uses setTimeout rather than setInterval so we can dynamically adjust the
 * delay — whenever the server turns a save down (403/429, or any other
 * non-2xx) we exponentially back off (10s → 20 → 40 → 80 → 160 → capped at
 * 300s) and toast only the first failure of the run. A rejected save used to
 * be re-sent every 10s with a fresh toast each time for as long as the tab
 * stayed open (#1752). The backoff resets to the 10s baseline on the first
 * successful save.
 */
export function startCloudAutoSave() {
  if (cloudSaveTimer) {
    clearTimeout(cloudSaveTimer);
    cloudSaveTimer = null;
  }
  cloudSaveBackoffMs = CLOUD_SAVE_BASE_MS;
  let lastCloudHash = "";
  let failing = false;

  const tick = async () => {
    // Skip autosave while the user is actively dragging — otherwise a tick
    // in the middle of a mousemove sequence ships a half-dragged state to
    // the server (sprinkler halfway between old and new position). When the
    // drag completes (onCanvasMouseUp clears isDragging) the next tick will
    // pick up the final state.
    //
    // Use the base interval (not the inflated backoff) here: if we've
    // previously throttled up to the 300s cap, dragging would otherwise
    // hold the tick at 300s instead of resuming the normal 10s cadence
    // once the user lets go. The drag-skip branch never contacts the
    // server, so it must not compound the backoff window.
    if (state.isDragging) {
      cloudSaveTimer = setTimeout(tick, CLOUD_SAVE_BASE_MS);
      return;
    }

    const json = JSON.stringify(getProjectDataForAPI());
    if (json !== lastCloudHash) {
      const result = await saveProjectToAPI({ notify: !failing });
      if (result === "ok") {
        lastCloudHash = json;
        cloudSaveBackoffMs = CLOUD_SAVE_BASE_MS;
        failing = false;
      } else if (result === "throttled" || result === "rejected") {
        cloudSaveBackoffMs = Math.min(cloudSaveBackoffMs * 2, CLOUD_SAVE_MAX_BACKOFF_MS);
        failing = true;
      } else if (result === "conflict") {
        // Another tab or device saved since this copy was loaded; any further
        // save would overwrite it. Stay stopped until the user reloads.
        cloudSaveTimer = null;
        return;
      }
    }
    cloudSaveTimer = setTimeout(tick, cloudSaveBackoffMs);
  };
  cloudSaveTimer = setTimeout(tick, cloudSaveBackoffMs);
}

/**
 * Final save on tab close, called from app.ts's beforeunload listener.
 *
 * Returns "clean" when the server already has this plan, "sent" when a
 * keepalive PATCH carries the edits past unload, and "unsent" when the edits
 * cannot leave the page in time — the listener then asks the browser to
 * confirm leaving, and the ordinary save started here finishes if the user
 * stays.
 *
 * Only `fetch(keepalive)` can deliver a PATCH during unload, and browsers cap
 * keepalive bodies at 64 KB (rejecting larger ones asynchronously, unseen).
 * sendBeacon is no way round it: it always POSTs, and there is no POST route
 * for a project (405), besides sharing the same 64 KB quota — so plans over
 * the threshold (every DXF import) used to lose their last edits silently
 * (#1752).
 */
export function flushCloudAutoSave(): "clean" | "sent" | "unsent" {
  if (!currentProjectId || !isAuthenticated()) return "clean";

  const data = getProjectDataForAPI();
  if (lastCloudSavedJson === null || JSON.stringify(data) === lastCloudSavedJson) return "clean";

  const body = JSON.stringify(currentProjectVersion ? { data, expected_updated_at: currentProjectVersion } : { data });
  // A margin under the 64 KB keepalive cap.
  const KEEPALIVE_MAX_BYTES = 60_000;
  if (body.length > KEEPALIVE_MAX_BYTES) {
    void saveProjectToAPI({ notify: false });
    return "unsent";
  }

  try {
    fetch(api_v1_project_path(currentProjectId), {
      method: "PATCH",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body,
    }).catch(() => { /* best-effort — no handler in the unload path */ });
    return "sent";
  } catch {
    void saveProjectToAPI({ notify: false });
    return "unsent";
  }
}
